import {
  type CalendarItem,
  type CalendarView,
  DEFAULT_SETTINGS,
  type FeedItem,
} from "@ronin/contracts";
import { localNow } from "@ronin/core";
import { Button, Kbd, SegmentedControl } from "@ronin/ui";
import { useQuery } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import { CalendarCheck, ChevronLeft, ChevronRight, Loader2, Plus, Rss } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { EventDialog, type EventDialogState } from "../components/calendar/event-dialog.js";
import { FeedBar } from "../components/calendar/feed-bar.js";
import { FeedsDialog } from "../components/calendar/feeds-dialog.js";
import { ItemDialog } from "../components/calendar/item-dialog.js";
import { MonthView } from "../components/calendar/month-view.js";
import { TimeGrid } from "../components/calendar/time-grid.js";
import { useCalendarData, useEventMutations } from "../components/calendar/use-calendar.js";
import { NeedsConnection } from "../components/needs-connection.js";
import { settingsQuery } from "../components/settings/use-settings.js";
import { visibleItems } from "../lib/calendar-items.js";
import { periodTitle, stepDate, visibleRange } from "../lib/calendar-view.js";
import { needsConnection } from "../lib/errors.js";
import { isTyping } from "../lib/keyboard.js";
import { useMediaQuery } from "../lib/use-media-query.js";
import { useSwipe } from "../lib/use-swipe.js";

const route = getRouteApi("/app/calendar");

const views: ReadonlyArray<{ value: CalendarView; label: string }> = [
  { value: "month", label: "Month" },
  { value: "week", label: "Week" },
  { value: "day", label: "Day" },
];

const viewKeys: Record<string, CalendarView> = { m: "month", w: "week", d: "day" };

/** Calendar (spec03): month, week and day views; view and date live in the URL (FR1). */
export function CalendarPage() {
  const search = route.useSearch();
  const navigate = route.useNavigate();
  const settings = useQuery(settingsQuery).data ?? DEFAULT_SETTINGS;
  const timeZone = settings.timezone;
  const today = localNow(new Date(), timeZone).date;
  const view = search.view ?? settings.calendarDefaultView;
  const date = search.date ?? today;
  const compact = !useMediaQuery("(min-width: 768px)");
  const range = useMemo(
    () => visibleRange(view, date, settings.weekStart),
    [view, date, settings.weekStart],
  );

  const { items, feeds } = useCalendarData(range.from, range.to);
  const feedList = feeds.data ?? [];
  const feedMap = useMemo(() => new Map(feedList.map((f) => [f.id, f])), [feedList]);
  const shown = useMemo(
    () => (feeds.data ? visibleItems(items.data ?? [], feeds.data) : (items.data ?? [])),
    [items.data, feeds.data],
  );
  const events = useEventMutations();

  const [eventDialog, setEventDialog] = useState<EventDialogState>({ mode: "closed" });
  const [feedItem, setFeedItem] = useState<FeedItem | null>(null);
  const [feedsOpen, setFeedsOpen] = useState(false);

  const go = (next: { view?: CalendarView; date?: string }) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }) });
  const step = (direction: 1 | -1) => go({ date: stepDate(view, date, direction) });
  const box = useRef<HTMLElement>(null);
  useSwipe(box, (direction) => step(direction === "left" ? 1 : -1));
  const newEvent = () =>
    setEventDialog({
      mode: "create",
      date: range.days.includes(today) ? today : date,
      time: view === "month" ? undefined : "09:00",
    });

  // FR5: N new event, T today, M/W/D views, ←/→ previous/next.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isTyping(event) || document.querySelector("dialog[open]")) return;
      const key = event.key.toLowerCase();
      const actions: Record<string, () => void> = {
        n: newEvent,
        t: () => go({ date: today }),
        arrowleft: () => step(-1),
        arrowright: () => step(1),
      };
      const nextView = viewKeys[key];
      const action = nextView ? () => go({ view: nextView }) : actions[key];
      if (!action) return;
      event.preventDefault();
      action();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  const openItem = (item: CalendarItem) => {
    if (item.source === "event") setEventDialog({ mode: "edit", event: item.event });
    else if (item.source === "feed") setFeedItem(item);
  };
  const openDay = (day: string) => go({ view: "day", date: day });
  const create = (day: string, time?: string) =>
    setEventDialog({ mode: "create", date: day, time });

  return (
    // A fixed-height page: month, week and day all fill the same box (no jumping between views).
    // Phones: edge to edge between the top and the tab bar (spec03 FR6).
    <div className="-mx-4 -my-4 flex h-[calc(100svh-3.5rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] min-h-[32rem] flex-col gap-2 sm:-mx-8 sm:-my-8 md:mx-0 md:my-0 md:h-[calc(100svh-11rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] md:w-full md:gap-4">
      {compact ? (
        // App bar: title and icon actions, the view switch full width below.
        <header className="flex flex-col gap-2 px-3 pt-2">
          <div className="flex items-center gap-1">
            <h1
              className="min-w-0 flex-1 truncate text-base font-semibold text-foreground"
              aria-live="polite"
            >
              {periodTitle(view, date, range)}
            </h1>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Today"
              title="Today"
              onClick={() => go({ date: today })}
            >
              <CalendarCheck />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Feeds"
              title="Feeds"
              onClick={() => setFeedsOpen(true)}
            >
              <Rss />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label="New event"
              title="New event"
              onClick={newEvent}
            >
              <Plus />
            </Button>
          </div>
          <SegmentedControl
            className="w-full *:flex-1"
            aria-label="View"
            value={view}
            options={views}
            onValueChange={(v) => go({ view: v })}
          />
        </header>
      ) : (
        // The title has a fixed width (the longest period title fits), so the arrows and
        // the view switch never move when the period or view changes.
        <header className="flex flex-wrap items-center gap-x-3 gap-y-2 md:flex-nowrap">
          <h1
            className="w-full truncate text-lg font-semibold text-foreground sm:w-72 sm:shrink-0"
            aria-live="polite"
          >
            {periodTitle(view, date, range)}
          </h1>
          <div className="flex shrink-0 items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Previous"
              title="Previous (←)"
              onClick={() => step(-1)}
            >
              <ChevronLeft />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Next"
              title="Next (→)"
              onClick={() => step(1)}
            >
              <ChevronRight />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="ml-1"
              onClick={() => go({ date: today })}
              title="Today (T)"
            >
              Today
            </Button>
          </div>
          <SegmentedControl
            className="shrink-0"
            aria-label="View"
            value={view}
            options={views}
            onValueChange={(v) => go({ view: v })}
          />
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <Button variant="secondary" onClick={() => setFeedsOpen(true)}>
              <Rss />
              Feeds
            </Button>
            <Button onClick={newEvent}>
              <Plus />
              New event
              <Kbd className="hidden sm:inline-flex">N</Kbd>
            </Button>
          </div>
        </header>
      )}

      {!compact && <FeedBar feeds={feedList} />}

      {needsConnection(items) ? (
        <NeedsConnection onRetry={() => void items.refetch()} />
      ) : (
        items.isError && (
          <p className="border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            Could not load the calendar. Try reloading the page.
          </p>
        )
      )}
      <section
        ref={box}
        aria-label="Calendar"
        className="relative min-h-0 flex-1 touch-pan-y max-md:*:border-x-0 max-md:*:border-b-0"
      >
        {items.isPending && !needsConnection(items) && (
          <p className="absolute top-2 right-3 z-20 flex items-center gap-2 text-xs text-dim-foreground">
            <Loader2 className="size-3.5 animate-spin" aria-hidden />
            Loading…
          </p>
        )}
        {view === "month" ? (
          <MonthView
            date={date}
            days={range.days}
            items={shown}
            feeds={feedMap}
            timeZone={timeZone}
            today={today}
            weekStart={settings.weekStart}
            compact={compact}
            onCreate={(day) => create(day)}
            onOpenItem={openItem}
            onOpenDay={openDay}
          />
        ) : (
          <TimeGrid
            days={range.days}
            items={shown}
            feeds={feedMap}
            timeZone={timeZone}
            today={today}
            compact={compact}
            onCreate={create}
            onOpenItem={openItem}
            onOpenDay={openDay}
          />
        )}
      </section>

      <EventDialog
        state={eventDialog}
        timeZone={timeZone}
        onClose={() => setEventDialog({ mode: "closed" })}
        onSubmit={async (input, event) => {
          if (event) await events.update.mutateAsync({ id: event.id, input });
          else await events.create.mutateAsync(input);
          setEventDialog({ mode: "closed" });
        }}
        onDelete={(event) => {
          setEventDialog({ mode: "closed" });
          events.remove.mutate(event);
        }}
      />
      <ItemDialog
        item={feedItem}
        feeds={feedMap}
        timeZone={timeZone}
        onClose={() => setFeedItem(null)}
      />
      <FeedsDialog open={feedsOpen} feeds={feedList} onOpenChange={setFeedsOpen} />
    </div>
  );
}
